import useSWR, { mutate } from 'swr';
import { AxiosError } from 'axios';
import { getAllDimensions, getDimensionById } from '~/app/shared/dimensions/api';
import { Dimension, DimensionsAccess } from '~/app/shared/dimensions/types';
import { dimensionQueryKeyMap } from '~/app/shared/dimensions/dimensionQueryKeyFactory';
import useDimensionsAccessLevel from './useDimensionsAccessLevel';

interface DimensionsQueryProps {
    includeDistributionKeys?: boolean;
    includeBarredLines?: boolean;
    includeBarredDimensions?: boolean;
    initialData?: Dimension[];
    skipLines?: boolean;
    skip?: boolean;
}

export interface DimensionsQueryResponse {
    data: Dimension[];
    error?: AxiosError;
    /** True if there's an ongoing request (initial fetch or revalidation). */
    validating: boolean;
    /** True if no data has been fetched yet (initial fetch). */
    loading: boolean;
    /** True if the initial fetch has been completed. */
    __initialized: boolean;
    /** Returns dimension name or provided substitute string */
    getDimensionName: (dimensionId: number, substitute?: string) => string;
}

/**
 * Hook to get all dimensions, using {@link useSWR}.
 * By default, it returns all dimensions <b>with</b> distribution keys, <b>without</b> barred lines and dimensions.
 * Initial data defaults to an empty array.
 */
const useDimensions = (config: DimensionsQueryProps = {}): DimensionsQueryResponse => {
    const {
        includeDistributionKeys,
        includeBarredLines,
        includeBarredDimensions,
        initialData,
        skip,
    }: Required<DimensionsQueryProps> = {
        includeDistributionKeys: true,
        includeBarredLines: false,
        includeBarredDimensions: false,
        initialData: [],
        skipLines: false,
        skip: false,
        ...config,
    };
    const hasDimension = useDimensionsAccessLevel(DimensionsAccess.HasDimension);

    const fetcher = _ =>
        getAllDimensions(includeDistributionKeys, includeBarredDimensions, includeBarredLines).then(res => res.data);

    const key = !hasDimension || skip ? null : ['dim/all'];
    if (includeDistributionKeys) key?.push('dim/withDistKeys');
    if (includeBarredLines) key?.push('dim/withBarredLines');
    if (includeBarredDimensions) key?.push('dim/withBarredDims');

    const response = useSWR(key, fetcher, { fallbackData: initialData, keepPreviousData: true });
    const getDimensionName = (dimensionId: number, substitute = '...'): string =>
        response.isLoading || !response.data.filter(d => d.id === dimensionId)[0]?.name
            ? substitute
            : response.data.filter(d => d.id === dimensionId)[0].name;

    return {
        data: response.data,
        error: response.error as AxiosError,
        validating: response.isValidating,
        loading: response.isLoading,
        __initialized: !(response.isLoading && !response.data),
        getDimensionName,
    };
};

type DimensionQueryProps = Omit<DimensionsQueryProps, 'includeBarredDimensions'> & { initialData?: Dimension };
interface DimensionQueryResponse {
    data: Dimension | undefined;
    error?: AxiosError;
    /** True if there's an ongoing request (initial fetch or revalidation). */
    validating: boolean;
    /** True if no data has been fetched yet (initial fetch). */
    loading: boolean;
    /** True if the initial fetch has been completed. */
    __initialized: boolean;
}
export const useOneDimension = (id: number, config?: DimensionQueryProps): DimensionQueryResponse => {
    const { includeDistributionKeys, includeBarredLines, initialData, skip, skipLines }: DimensionQueryProps = {
        includeDistributionKeys: true,
        includeBarredLines: false,
        initialData: undefined,
        skipLines: false,
        skip: false,
        ...config,
    };
    const hasDimension = useDimensionsAccessLevel(DimensionsAccess.HasDimension);
    const fetcher = _ => getDimensionById(id, includeBarredLines, includeDistributionKeys, skipLines).then(res => res.data);

    const key = !hasDimension || skip ? null : [`dim/${id}`];
    if (includeDistributionKeys) key?.push('dim/withDistKeys');
    if (includeBarredLines) key?.push('dim/withBarredLines');

    const response = useSWR(key, fetcher, { fallbackData: initialData, keepPreviousData: true });

    return {
        data: response.data,
        error: response.error as AxiosError,
        validating: response.isValidating,
        loading: response.isLoading,
        __initialized: !response.isLoading,
    };
};

type InvalidateDimensionCacheFnType = <K extends keyof typeof dimensionQueryKeyMap>(
    updatedEntityType: K,
    args: Parameters<(typeof dimensionQueryKeyMap)[K]>
) => Promise<unknown>;

/**
 * Invalidates the cache for the specified dimension / value / distribution key.
 * Use this function to update the cache after a dimension entity has been updated.
 * */
export const invalidateDimensionCache: InvalidateDimensionCacheFnType = (updatedEntityType, args) => {
    // @ts-expect-error - TypeScript assumes there's only one argument, but args is an array
    const keysToInvalidate = dimensionQueryKeyMap[updatedEntityType](...args);
    return mutate(key => {
        if (typeof key === 'string') return keysToInvalidate.includes(key);
        if (Array.isArray(key)) return keysToInvalidate.some(k => key.includes(k));
        return false;
    });
};

export default useDimensions;
